/**
 * The ranger on screen: his soldier (in the ghillie wrap when he carries one), the stolen uniform he may be
 * wearing instead, and every way he is placed - hanging under the canopy, on the ladder, dragged through the
 * wire, on his feet. Split out of `Actors.ts` in ROUND8 to make room for the dogs; `Actors` still hands him the
 * throw and the knife thrust it hears of in the events (`shot`, `stabFacing`), and calls `update` first each
 * frame, so his is the first mesh in the scene.
 */

import type { Material, Scene, Texture } from 'three';
import { RANGER } from '../../sim/tuning';
import type { World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { crouchFor, DISGUISE_LOOK, lungeAt, rangerLook } from '../characters/rangerLook';
import { Soldier } from '../characters/soldier';
import { createToonMaterial, OUTLINE } from '../materials/toon';
import type { Palette } from '../palette';
import { type Fall, type OneShot, poseRanger } from './actorMotion';
import { flightHeight } from './altitude';
import { HOVER_HEIGHT } from './OspreyView';

/** How far a chest can turn from the hips, radians. */
const MAX_TWIST = 1.4;
/** He steps onto the ladder over the first fifth of the climb, then goes up it. */
const LADDER_STEP_ON = 0.2;
/** How far up the ladder he is when the sim takes him aboard: his hands are at the ramp. */
const LADDER_TOP = HOVER_HEIGHT - 1.6;

function angleDiff(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** The turn of a man's chest from `facing` toward `aim`, as far as a spine allows. */
export const twistToward = (facing: number, aim: number): number =>
  Math.max(-MAX_TWIST, Math.min(MAX_TWIST, angleDiff(facing, aim)));

/** Stand a soldier at his interpolated spot on the ground. `sink` lowers him into a trench; negative lifts him. */
export function placeSoldier(
  soldier: Soldier, groundY: (x: number, z: number) => number,
  px: number, pz: number, x: number, z: number, alpha: number, sink: number,
): void {
  const ix = px + (x - px) * alpha;
  const iz = pz + (z - pz) * alpha;
  soldier.state.position.set(ix, groundY(ix, iz) - sink, iz);
}

/** Take one soldier out of the scene and free what only he holds (the materials are shared). */
export function discardSoldier(scene: Scene, soldier: Soldier): void {
  scene.remove(soldier.mesh);
  soldier.mesh.geometry.dispose();
  soldier.mesh.skeleton.dispose();
}

export class RangerActor {
  private readonly soldier: Soldier;
  /** Whether `soldier` is dressed in the ghillie wrap: re-dressed when the sim's gear says otherwise. */
  private ghillie = false;
  private disguise: Soldier | null = null;
  /** The player's outline class: the disguise keeps it too, so he keeps his night rim. */
  private readonly material: Material;
  /** His throw or knife thrust in progress, handed over by `Actors.handle`. */
  shot: OneShot | null = null;
  /** Which way his last stab went: at the man it found (the `knife` event's point), not along the aim. */
  stabFacing: number | null = null;
  private fall: Fall | null = null;
  private wasAlive = true;

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    gradient: Texture,
    private readonly groundY: (x: number, z: number) => number,
  ) {
    this.material = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.player });
    this.soldier = new Soldier(palette, this.material, rangerLook(false));
    scene.add(this.soldier.mesh);
  }

  /** The ranger's body for this frame. The disguise keeps the PLAYER material, so he keeps his night rim. */
  private body(disguised: boolean): Soldier {
    if (this.disguise) this.disguise.mesh.visible = false;
    this.soldier.mesh.visible = false;
    if (!disguised) return this.soldier;
    if (!this.disguise) {
      this.disguise = new Soldier(this.palette, this.material, DISGUISE_LOOK);
      this.scene.add(this.disguise.mesh);
    }
    return this.disguise;
  }

  private place(body: Soldier, px: number, pz: number, x: number, z: number, alpha: number, sink: number): void {
    placeSoldier(body, this.groundY, px, pz, x, z, alpha, sink);
  }

  update(world: World, time: number, alpha: number, camera: PixelCamera): void {
    const r = world.ranger;
    const ins = world.insertion;
    const ghillie = r.gear.ghillie === true;
    if (ghillie !== this.ghillie) this.soldier.redress(this.palette, rangerLook(ghillie));
    this.ghillie = ghillie;
    const body = this.body(world.objective.disguised);
    // Aboard the aircraft he is not drawn; InsertionView owns the aircraft, canopy and pods.
    if (ins.phase === 'flyover') return;
    body.mesh.visible = r.status !== 'extracted' && r.status !== 'walkedOut';
    const s = body.state;
    body.clearOverlays();
    if (r.status === 'kia' && this.wasAlive) {
      // Shot on his feet he goes down; a man already down (the usual way to die) just stops moving.
      this.fall = r.stance === 'stand' ? { start: time, blast: r.hurtBy === 'blast', facing: null } : null;
    }
    this.wasAlive = r.status !== 'kia';
    if (ins.phase === 'descent') {
      // Hanging under the parafoil: limp, feet at his height above the ground, turned to his heading.
      // The same level line as his canopy: he must not dip into every trench he drifts over.
      this.place(body, r.prevX, r.prevZ, r.x, r.z, alpha, 0);
      const p = body.state.position;
      p.y = flightHeight(p.y, ins.altitude);
      s.facing = ins.heading;
      s.motion = 'idle';
      s.twist = 0;
      s.reverse = false;
    } else if (r.action === 'board') {
      // Onto the ladder under the ramp, back to the camera, and up it as the sim counts the climb.
      const k = Math.min(1, r.actionTicks / RANGER.boardTicks);
      const on = Math.min(1, k / LADDER_STEP_ON);
      const x = r.x + (world.objective.pickupX - r.x) * on;
      const z = r.z + (world.objective.pickupZ + 0.25 - r.z) * on;
      const climbed = Math.max(0, (k - LADDER_STEP_ON) / (1 - LADDER_STEP_ON)) * LADDER_TOP;
      this.place(body, x, z, x, z, 1, -climbed);
      s.facing = Math.PI;
      s.twist = 0;
      s.reverse = false;
      poseRanger(s, r, time, null, null);
    } else if (world.hazards.wireTicks > 0) {
      this.place(body, r.prevX, r.prevZ, r.x, r.z, alpha, 0);
      const exit = world.hazards.wireExit;
      s.facing = exit ? Math.atan2(exit.x - r.x, exit.z - r.z) : r.facing;
      // The sim moves him without counting it as travel: the crawl cycle runs on the clock instead.
      s.travelled = time * 0.5;
      s.twist = 0;
      s.reverse = false;
      s.motion = 'crawl';
      s.age = 0;
    } else {
      this.place(body, r.prevX, r.prevZ, r.x, r.z, alpha, r.inTrench && r.stance === 'crawl' ? 0.1 : 0);
      s.facing = r.facing;
      s.travelled = r.travelled;
      s.twist = twistToward(r.facing, r.aim);
      s.reverse = r.backpedal;
      poseRanger(s, r, time, this.shot, this.fall);
      crouchFor(s, r.sneaking === true);
      lungeAt(s, this.stabFacing);
    }
    body.update(time, camera);
  }

  /** Remove him (and the disguise) from the scene and free the player's material. */
  dispose(): void {
    discardSoldier(this.scene, this.soldier);
    if (this.disguise) discardSoldier(this.scene, this.disguise);
    this.disguise = null;
    this.material.dispose();
  }
}
